import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import "@/Dashboard.css";
import jsPDF from "jspdf";

import ContractsTab, { CONTRACT_TEMPLATES, CEO_CONTRACTS, COO_CONTRACTS } from "./components/dashboard/ContractsTab";
import ProspectsTab from "./components/dashboard/ProspectsTab";
import FieldToolsTab from "./components/dashboard/FieldToolsTab";
import ProjectsTab, { ClientsTab } from "./components/dashboard/ProjectsTab";
import HomeTab from "./components/dashboard/HomeTab";
import Cockpit from "./components/dashboard/Cockpit";
import { SandboxBanner } from "./components/SandboxBanner";
import { QuickCaptureFAB } from "./components/dashboard/FounderFire";
import { OperationsTab, ComplianceTab, QuickStartWelcome, PayrollTab, TaskBoardTab, LaunchReadinessTab, COOWelcomeGuide, RevenueTab, CrewScheduleTab, LAUNCH_CATEGORIES } from "./components/dashboard/SmallTabs";
import CertificationsTab from "./components/dashboard/CertificationsTab";
import PitchCenterTab from "./components/dashboard/PitchCenterTab";
import GrantsTab from "./components/dashboard/GrantsTab";
import LeadPaintRosterTab from "./components/dashboard/LeadPaintRosterTab";
import LaunchChecklistTab from "./components/dashboard/LaunchChecklistTab";
import PreflightModal from "./components/dashboard/PreflightModal";
import DashboardAgent from "./components/dashboard/DashboardAgent";
import BriefRequestsTab from "./components/dashboard/BriefRequestsTab";
import OutreachAutoPilotTab from "./components/dashboard/OutreachAutoPilotTab";
import VoucherCoalitionsTab from "./components/dashboard/VoucherCoalitionsTab";
import ClientMessagesTab from "./components/dashboard/ClientMessagesTab";
import SourcedProductsTab from "./components/dashboard/SourcedProductsTab";
import FounderDrawTab from "./components/dashboard/FounderDrawTab";
import ScriptsLibraryTab from "./components/dashboard/ScriptsLibraryTab";
import VendorNetworkTab from "./components/dashboard/VendorNetworkTab";
import ProDesksTab from "./components/dashboard/ProDesksTab";
import FieldLauncherTab from "./components/dashboard/FieldLauncherTab";
import InspectionHome from "./components/inspection/InspectionHome";
import ReadyRosterTab from "./components/dashboard/ReadyRosterTab";
import { TemplatesLibraryTab, PaletteSpotlightTab } from "./components/dashboard/TemplatesAndSpotlight";
import MoovinAiAssistant from "./components/MoovinAiAssistant";

const BACKEND_URL = "";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";

const STATUS_OPTIONS = ["Submitted", "Assessed", "Proposal Sent", "In Progress", "Turnover Complete", "Complete", "Listed", "Leased", "On Hold", "Cancelled"];

// ===== Auth Check =====
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(u => { setUser(u); setLoading(false); })
      .catch(() => setLoading(false));
  }, [navigate]);

  return { user, loading };
};

// ===== Main Dashboard =====
// RADICAL SIMPLIFICATION — Feb 11, 2026 (Shannon: "fully congested with a lot
// of stuff that doesn't look cohesive"). Collapsed to 5 hubs. Legacy
// "hub-home (Old)" removed. Escape-hatch "All Tabs" button removed.
const ALL_TABS = [
  // Hub 1: Cockpit
  { key: "home", label: "Cockpit", homeStyle: true, cat: "core", hub: "home" },

  // Hub 2: Design + Projects
  { key: "design-studio", label: "🎨 Design Studio", externalPath: "/design-studio", cat: "tools", hub: "design" },
  { key: "projects", label: "🏗 Projects", cat: "ops", hub: "design" },
  { key: "field-mode", label: "🛠 Field Mode", cat: "ops", hub: "design" },
  { key: "templates-library", label: "🧱 Templates", cat: "tools", hub: "design" },

  // Hub 3: Vendor Network
  { key: "vendor-network", label: "🎯 Vendor Network", cat: "ops", hub: "vendors" },
  { key: "ready-roster", label: "Ready Roster", cat: "ops", hub: "vendors" },
  { key: "prospects", label: "Prospects", cat: "sales", hub: "vendors" },
  { key: "contracts", label: "Contracts", cat: "sales", hub: "vendors" },
  { key: "lead-paint-roster", label: "🛡️ Lead-Paint Roster", cat: "sales", hub: "vendors" },
  { key: "clients", label: "Clients", cat: "ops", hub: "vendors" },

  // Hub 4: Money
  { key: "revenue", label: "Revenue", cat: "admin", hub: "money" },
  { key: "payroll", label: "Payroll", cat: "ops", hub: "money" },
  { key: "founder-draw", label: "Founder Draw", cat: "tools", hub: "money" },
  { key: "grants", label: "💰 Grants", cat: "sales", hub: "money" },
  { key: "certifications", label: "Certifications", cat: "admin", hub: "money" },
  { key: "compliance", label: "Compliance", cat: "admin", hub: "money" },

  // Hub 5: Advanced (everything else)
  { key: "tasks", label: "Tasks", cat: "ops", hub: "advanced" },
  { key: "schedule", label: "Schedule", cat: "ops", hub: "advanced" },
  { key: "messages", label: "Messages", cat: "sales", hub: "advanced" },
  { key: "pitch-center", label: "Pitch Center", cat: "sales", hub: "advanced" },
  { key: "auto-pilot", label: "Auto-Pilot", cat: "sales", hub: "advanced" },
  { key: "voucher-coalitions", label: "Voucher Coalitions", cat: "sales", hub: "advanced" },
  { key: "brief-requests", label: "Brief Requests", cat: "sales", hub: "advanced" },
  { key: "sourced", label: "My Products", cat: "tools", hub: "advanced" },
  { key: "scripts", label: "Scripts", cat: "tools", hub: "advanced" },
  { key: "palette-spotlight", label: "📣 Variation Spotlight", cat: "sales", hub: "advanced" },
  { key: "tools", label: "Field Tools", cat: "tools", hub: "advanced" },
  { key: "pro-desks", label: "🏪 Pro Desks", cat: "tools", hub: "advanced" },
  { key: "operations", label: "Operations", cat: "admin", hub: "advanced" },
  { key: "launch", label: "Launch", cat: "admin", hub: "advanced" },
];

// 5-Hub structure — Shannon's radical simplification directive (Feb 11, 2026)
const HUBS = [
  { key: "home",     label: "Cockpit",           icon: "fa-house",         color: "#00FFFF", desc: "Everything you need is right here" },
  { key: "design",   label: "Design + Projects", icon: "fa-palette",       color: "#FFB347", desc: "Design Studio · Projects · Field Mode · Templates" },
  { key: "vendors",  label: "Vendor Network",    icon: "fa-users",         color: "#5AC8D8", desc: "Applications · Contracts · Prospects · Clients" },
  { key: "money",    label: "Money",             icon: "fa-shield-halved", color: "#00DC78", desc: "Revenue · Payroll · Grants · Certifications" },
  { key: "advanced", label: "Advanced",          icon: "fa-gear",          color: "#94A3B8", desc: "Auto-Pilot · Voucher Coalitions · Pitch Center · Ops · Launch" },
];

// Shannon (CEO) and Shaneka (Administrator) both see the same tabs — they collaborate on everything.
// Per Feb 12, 2026 doctrine refinement: Shaneka's role is Administrator, not COO. COO functions
// delegate to the digital platform (backend engines). She inherits the Moovin Forward Cockpit
// layout — clean dashboard, no redundant studios, no extra navigation layers.
const ALL_TAB_KEYS = ALL_TABS.filter(t => !t.divider).map(t => t.key);
const CEO_TABS = ALL_TAB_KEYS;
const ADMIN_TABS = ALL_TAB_KEYS;

const getTabsForRole = (dashboardRole) => {
  if (dashboardRole === "ceo") return ALL_TABS.filter(t => CEO_TABS.includes(t.key));
  if (dashboardRole === "administrator" || dashboardRole === "coo")
    return ALL_TABS.filter(t => ADMIN_TABS.includes(t.key));
  return ALL_TABS;
};

const getRoleLabel = (dashboardRole) => {
  if (dashboardRole === "ceo") return "CEO";
  if (dashboardRole === "administrator" || dashboardRole === "coo") return "Administrator";
  return "Admin";
};

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined" && window.location.hash) {
      const h = window.location.hash.replace("#", "");
      // Only allow whitelisted hash targets
      if (["tools", "templates-library", "palette-spotlight", "design-studio", "vendor-network"].includes(h)) {
        return h;
      }
    }
    return "home";
  });
  const [activeHub, setActiveHub] = useState(() => {
    // If navigating in via hash to a tab, switch to the new 5-hub target
    if (typeof window !== "undefined" && window.location.hash) {
      const h = window.location.hash.replace("#", "");
      if (h === "vendor-network") return "vendors";
      if (h === "design-studio" || h === "templates-library" || h === "tools") return "design";
    }
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("mf_active_hub");
      // Migrate any old hub keys to the new 5-hub set
      const legacy = { "get-clients": "vendors", "run-projects": "design", "design-source": "design", "money-legal": "money", "settings": "advanced" };
      if (stored && legacy[stored]) return legacy[stored];
      return stored || "home";
    }
    return "home";
  });
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [showPreflight, setShowPreflight] = useState(false);
  const [preflightOverall, setPreflightOverall] = useState(null);  // 'green' | 'yellow' | 'red' | null

  // Run Pre-Flight quietly on dashboard mount so the nav badge surfaces system health at a glance
  useEffect(() => {
    let cancelled = false;
    const runQuiet = async () => {
      try {
        const r = await fetch(`/api/admin/preflight`, { credentials: "include" });
        if (!cancelled && r.ok) {
          const d = await r.json();
          setPreflightOverall(d.overall || null);
        }
      } catch (_) { /* silent — modal will retry if user clicks */ }
    };
    runQuiet();
    return () => { cancelled = true; };
  }, []);
  const [unreadShared, setUnreadShared] = useState(0);
  const [showCOOGuide, setShowCOOGuide] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [autoPilotBadge, setAutoPilotBadge] = useState({ count: 0, is_paused: false });
  const [trainingMode, setTrainingMode] = useState(() => {
    // Default ON; persist user preference across sessions
    const stored = typeof window !== "undefined" ? localStorage.getItem("mf_training_mode") : null;
    return stored === null ? true : stored === "1";
  });

  const toggleTrainingMode = () => {
    setTrainingMode(prev => {
      const next = !prev;
      localStorage.setItem("mf_training_mode", next ? "1" : "0");
      return next;
    });
  };

  const dashboardRole = user?.dashboard_role || "";
  const visibleTabs = getTabsForRole(dashboardRole);

  // Filter tabs by active hub for the simplified 5-Hub navigation
  // "all" = legacy power-user view (shows everything flat)
  const hubFilteredTabs = activeHub === "all"
    ? visibleTabs
    : visibleTabs.filter(t => t.divider || (t.hubs ? t.hubs.includes(activeHub) : t.hub === activeHub) || (activeHub === "home" && t.hub === "home"));

  const setHub = (hubKey) => {
    setActiveHub(hubKey);
    if (typeof window !== "undefined") localStorage.setItem("mf_active_hub", hubKey);
    // Auto-jump to the first non-divider tab in that hub if current tab is not in hub
    if (hubKey !== "all") {
      const hubTabs = visibleTabs.filter(t => !t.divider && (t.hubs ? t.hubs.includes(hubKey) : t.hub === hubKey));
      if (hubTabs.length > 0 && !hubTabs.some(t => t.key === tab)) {
        const firstTab = hubTabs[0];
        if (firstTab.externalPath) navigate(firstTab.externalPath);
        else setTab(firstTab.key);
      }
    }
  };

  useEffect(() => {
    if (user && !localStorage.getItem("mf_quickstart_seen")) {
      setShowQuickStart(true);
    }
    if (user && (dashboardRole === "coo" || dashboardRole === "administrator") && !localStorage.getItem("mf_admin_guide_v2_seen")) {
      setShowCOOGuide(true);
    }
    if (user && !localStorage.getItem("mf_whatsnew_v6_seen")) {
      setShowWhatsNew(true);
    }
  }, [user, dashboardRole]);

  // Safe in-app navigation between dashboard tabs (no page reload).
  // Child components dispatch: window.dispatchEvent(new CustomEvent("mf-switch-tab", { detail: { hub, tab } }))
  // Replaces all `window.location.href = "/dashboard#..."` hard reloads that
  // were kicking the user out to a "site can't be reached" page on production.
  useEffect(() => {
    const handler = (e) => {
      const { hub, tab: targetTab } = e.detail || {};
      if (hub) {
        setActiveHub(hub);
        if (typeof window !== "undefined") localStorage.setItem("mf_active_hub", hub);
      }
      if (targetTab) setTab(targetTab);
      // Scroll to top so the user sees the new tab content
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("mf-switch-tab", handler);
    return () => window.removeEventListener("mf-switch-tab", handler);
  }, []);

  // Back-button history for in-dashboard tab/hub switches
  const navStack = useRef([]);
  const prevNav = useRef({ hub: activeHub, tab });
  const restoringNav = useRef(false);
  useEffect(() => {
    const prev = prevNav.current;
    if (prev.hub === activeHub && prev.tab === tab) return;
    if (!restoringNav.current) navStack.current.push(prev);
    restoringNav.current = false;
    prevNav.current = { hub: activeHub, tab };
  }, [activeHub, tab]);
  useEffect(() => {
    const onBack = (e) => {
      const last = navStack.current.pop();
      const target = last || ((tab !== "home" || activeHub !== "home") ? { hub: "home", tab: "home" } : null);
      if (!target) return;
      restoringNav.current = true;
      setActiveHub(target.hub);
      localStorage.setItem("mf_active_hub", target.hub);
      setTab(target.tab);
      window.scrollTo({ top: 0, behavior: "smooth" });
      e.detail.handled = true;
    };
    window.addEventListener("mf-nav-back", onBack);
    return () => window.removeEventListener("mf-nav-back", onBack);
  }, [tab, activeHub]);

  // Fetch unread shared items count
  useEffect(() => {
    if (!user || !dashboardRole) return;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/shared-with-me`, { credentials: "include" });
        if (r.ok) {
          const items = await r.json();
          setUnreadShared(items.filter(i => !i.read).length);
        }
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 120000);
    return () => clearInterval(interval);
  }, [user, dashboardRole]);

  // Fetch Auto-Pilot badge (today's queued count)
  useEffect(() => {
    if (!user || !dashboardRole) return;
    const fetchBadge = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/outreach/today-count`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          setAutoPilotBadge({ count: data.count || 0, is_paused: !!data.is_paused });
        }
      } catch {}
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 120000);
    return () => clearInterval(interval);
  }, [user, dashboardRole]);

  // Reset to home if current tab not in visible set
  useEffect(() => {
    const tabKeys = visibleTabs.filter(t => !t.divider).map(t => t.key);
    if (tab && !tabKeys.includes(tab)) setTab("home");
  }, [dashboardRole, visibleTabs, tab]);

  const dismissQuickStart = () => {
    setShowQuickStart(false);
    localStorage.setItem("mf_quickstart_seen", "1");
  };

  const dismissCOOGuide = () => {
    setShowCOOGuide(false);
    localStorage.setItem("mf_admin_guide_v2_seen", "1");
  };

  const dismissWhatsNew = () => {
    setShowWhatsNew(false);
    localStorage.setItem("mf_whatsnew_v6_seen", "1");
  };

  // Dashboard Search (token-based — each word in query must match somewhere in label/desc/keywords)
  const TAB_KEYWORDS = {
    "home": "dashboard overview welcome start",
    "next-steps": "todo onboarding what now first day setup",
    "projects": "jobs work properties units active",
    "tasks": "todo task board kanban",
    "clients": "customers team members shaneka shannon manage role admin",
    "payroll": "pay check 1099 paystubs crew wages",
    "schedule": "calendar appointments crew time slots",
    "contracts": "msa pilot dha agreements legal documents pdfs sign",
    "prospects": "leads outreach pipeline cold calls greystar dha fwhs amh evernest mynd target buyers",
    "pitch-center": "pitch deck presentation receivership institutional",
    "auto-pilot": "outreach auto pilot scheduled emails queue automation",
    "voucher-coalitions": "voucher housing authority coalition rad section 8",
    "messages": "client portal chat conversation 2 way",
    "sourced": "products amazon fittes epodex affiliate sourced inventory",
    "founder-draw": "salary owner draw take home pay calculator scenarios",
    "scripts": "ai saved scripts library template snippets",
    "design-studio": "design studio room visualizer before after palette colors paint visualization send prospect",
    "launch-protect": "launch checklist w9 pre lien dwc 5 non subscriber legal protect compliance",
    "brief-requests": "client briefs intake forms",
    "tools": "field tools nspire hqs walk through inspection calculator material",
    "vendor-network": "vendor network community lead paint pool safety hvac pest locksmith mold owner operator minority woman owned subcontractor partner",
    "pro-desks": "pro desk home depot lowes wayfair professional pro xtra rewards csv quick add cart order materials",
    "field-mode": "field mode cad cam crew cleaning maintenance launch shift session tally materials specs photos one button",
    "operations": "operations waves stabilization process",
    "compliance": "compliance trec epa rrp insurance",
    "launch": "launch readiness ship deploy",
    "revenue": "money invoices income earnings collected",
    "products": "products catalog sourced",
    "certifications": "certs osha epa nspire credentials",
  };

  const SEARCH_INDEX = [
    // Tabs (with rich keywords)
    ...ALL_TABS.filter(t => !t.divider).map(t => ({
      type: "tab",
      label: t.label,
      key: t.key,
      desc: `Go to ${t.label} tab`,
      keywords: TAB_KEYWORDS[t.key] || "",
    })),
    // Contracts
    ...Object.entries(CONTRACT_TEMPLATES).map(([key, tmpl]) => ({ type: "contract", label: tmpl.title, key, tab: "contracts", desc: tmpl.description?.slice(0, 80) || "", keywords: "" })),
    // Quick items
    { type: "action", label: "Dallas Housing Authority (DHA) — Prospect", key: "dha", tab: "prospects", desc: "Kenneth Duke, Director of Procurement. RAD conversions, 5-Year Capital Fund Plan, 3,200+ units + 19,000 vouchers. Re-engage Q3 or for next RFP cycle.", keywords: "kenneth duke procurement dallas dha housing authority rfp" },
    { type: "action", label: "DHA Kenneth Duke Contact", key: "dha", tab: "prospects", desc: "kenneth.duke@dhantx.com | (214) 951-8452 | procurement@dhantx.com", keywords: "kenneth duke dha" },
    { type: "action", label: "Fort Worth Housing Solutions", key: "fwhs", tab: "prospects", desc: "Kathy Bridwell — Procurement | (817) 333-3400", keywords: "fwhs tarrant" },
    { type: "action", label: "NSPIRE Inspection Checklist", key: "nspire", tab: "tools", desc: "47 items — HUD NSPIRE standards for unit interiors", keywords: "hud inspection" },
    { type: "action", label: "Section 8 / HQS Checklist", key: "hqs", tab: "tools", desc: "37 items — Housing Quality Standards for voucher eligibility", keywords: "voucher" },
    { type: "action", label: "311 Code Enforcement Checklist", key: "code311", tab: "tools", desc: "20 items — DFW code compliance", keywords: "code violations" },
    { type: "action", label: "Walk-Through Material Calculator", key: "calc", tab: "tools", desc: "Calculate materials, costs, and generate estimates", keywords: "estimate cost calculator" },
    { type: "action", label: "Onboard New Client", key: "onboard", tab: "home", desc: "Create a new client and project", keywords: "new client add customer" },
    { type: "action", label: "Prospect Intelligence Center", key: "prospects", tab: "prospects", desc: "30 DFW companies with talking points and one-click packages", keywords: "" },
    { type: "action", label: "Certifications Tracker", key: "certs", tab: "home", desc: "18 professional certifications — track yours and partner's progress", keywords: "" },
    { type: "action", label: "Shared Board (CEO ↔ COO)", key: "shared", tab: "home", desc: "Send notes, ideas, contracts between CEO and COO", keywords: "" },
    { type: "action", label: "Revenue Dashboard", key: "revenue", tab: "revenue", desc: "Revenue collected, outstanding, avg cost per unit", keywords: "" },
    { type: "action", label: "Crew Schedule", key: "schedule", tab: "schedule", desc: "Assign crew to properties with times and recurring options", keywords: "" },
    { type: "action", label: "Cancellation Policy", key: "cancel", tab: "home", desc: "1hr+ = no charge, <1hr = 50%, after arrival = 100%", keywords: "cancel late fee" },
    { type: "action", label: "Cleaning Services", key: "cleaning", tab: "tools", desc: "Deep cleaning, trash-out, and add-on services in calculator", keywords: "clean deep trash out" },
    { type: "action", label: "MSA / Master Service Agreement", key: "msa", tab: "contracts", desc: "14-section ironclad contract with sign-on/sign-off process", keywords: "" },
    { type: "action", label: "Pilot Program Agreement", key: "pilot", tab: "contracts", desc: "Zero-risk entry for first-time clients", keywords: "trial test" },
    { type: "action", label: "Business Structure PDF", key: "business_structure", tab: "contracts", desc: "Downloadable business structure document with QR code", keywords: "llc structure" },
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    const q = query.trim().toLowerCase();
    if (!q) { setSearchResults([]); return; }
    // Token-based — every word in query must appear somewhere in label, desc, or keywords
    const tokens = q.split(/\s+/).filter(Boolean);
    const results = SEARCH_INDEX.filter(item => {
      const haystack = `${item.label} ${item.desc} ${item.keywords || ""}`.toLowerCase();
      return tokens.every(tok => haystack.includes(tok));
    }).slice(0, 10);
    setSearchResults(results);
  };

  const handleSearchSelect = (result) => {
    if (result.type === "tab") {
      const tabDef = ALL_TABS.find(t => t.key === result.key);
      if (tabDef?.externalPath) { navigate(tabDef.externalPath); }
      else { setTab(result.key); }
    }
    else if (result.tab) {
      setTab(result.tab);
      // Some search results need to trigger an action AFTER the tab is set
      // (e.g., "Onboard New Client" opens a modal on the Home tab).
      // Defer so the target tab has time to mount its event listener.
      if (result.key === "onboard") {
        setTimeout(() => window.dispatchEvent(new CustomEvent("home:open-onboard")), 50);
      }
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleLogin = () => {
    localStorage.setItem("mf_auth_redirect", "dashboard");
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSetup = async () => {
    const r = await fetch(`${BACKEND_URL}/api/admin/setup`, {
      method: "POST", credentials: "include"
    });
    if (r.ok) window.location.reload();
  };

  if (loading) return <div className="dash-container"><div className="dash-loading">Loading...</div></div>;

  if (!user) {
    return (
      <div className="dash-container">
        <div className="dash-login-prompt" data-testid="dash-login-prompt">
          <img src={LOGO_URL} alt="Logo" className="dash-login-logo" />
          <h2>Team Dashboard</h2>
          <p>Sign in with your Google account to access the admin tools.</p>
          <button onClick={handleLogin} className="dash-google-btn" data-testid="dash-login-btn">Sign in with Google</button>
        </div>
      </div>
    );
  }

  // STRICT CEO ISOLATION (Feb 11, 2026 directive): CEO Dashboard is locked
  // to nivoomforward@gmail.com + slatricep@gmail.com. Everyone else — even
  // legacy "admin" role — is routed to the Crew Operations Portal.
  const CEO_EMAILS = ["nivoomforward@gmail.com", "slatricep@gmail.com"];
  const email = (user.email || "").toLowerCase();
  const isCeoAdmin = CEO_EMAILS.includes(email) || user.dashboard_role === "ceo" || user.dashboard_role === "coo" || user.dashboard_role === "administrator";
  const isAdmin = isCeoAdmin || user.role === "team";

  if (!isCeoAdmin) {
    // Non-CEO users are auto-redirected to the Crew Operations Portal —
    // no "Access Required" screen (that was confusing crew members).
    try { window.top.location.replace("/crew"); } catch { window.location.replace("/crew"); }
    return (
      <div className="dash-container">
        <div className="dash-login-prompt" data-testid="dash-redirect-crew">
          <h2>Redirecting to Crew Portal…</h2>
          <p>You're signed in as <strong>{user.email}</strong>. Taking you to the Crew Operations Portal.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dash-container ${trainingMode ? "training-on" : "training-off"}`} data-testid="admin-dashboard">
      {showQuickStart && <QuickStartWelcome user={user} onDismiss={dismissQuickStart} />}
      {showCOOGuide && <COOWelcomeGuide onDismiss={dismissCOOGuide} />}
      {showWhatsNew && (
        <div className="coo-guide-overlay" data-testid="whats-new-overlay">
          <div className="coo-guide-modal whats-new-modal">
            <h2>What's New in Your Dashboard</h2>
            <p className="coo-guide-intro">{dashboardRole === "ceo" ? "Shannon" : (dashboardRole === "administrator" || dashboardRole === "coo") ? "Shaneka" : "Team"}, here's what just got added to make your prospecting faster:</p>
            <div className="coo-guide-list">
              <div className="coo-guide-item" style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(0,255,255,0.08) 100%)", border: "1px solid rgba(167,139,250,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
                <strong style={{ color: "#A78BFA" }}>NEW — Voucher Coalitions Hub</strong>
                <p>15 curated programs covering HUD-VASH (veterans), VAWA / Emergency Housing Vouchers (DV survivors), Section 811 (disabilities), Section 202 (elderly), CoC rapid rehousing, FUP/FYI (family unification + foster youth), plus Dallas/Fort Worth women's coalitions: <strong>The Family Place, SafeHaven of Tarrant County, Genesis Women's Shelter, Mosaic Family Services, Family Gateway, Promise House</strong>, and the regional CoC lead agencies. Each card has a soft, warm intro email pre-written for you. Click <strong style={{ color: "#fff" }}>"Schedule via Auto-Pilot"</strong> on any card and it pre-fills the outreach with their contact, the angle, and the message — then queues to fire on the next optimal day. <em>This is your relationship-building tab — different from Prospects (which is sales-pitch heavy). Read the help card at the top of the new tab.</em></p>
              </div>
              <div className="coo-guide-item" style={{ background: "linear-gradient(135deg, rgba(0,220,120,0.08) 0%, rgba(0,255,255,0.08) 100%)", border: "1px solid rgba(0,220,120,0.30)", padding: "1rem 1.1rem", borderRadius: 8 }}>
                <strong style={{ color: "#00DC78" }}>NEW — Outreach Auto-Pilot</strong>
                <p>Queue pitches and visualization previews to fire automatically on Tue/Wed/Thu mornings (Central) — even while traveling. Auto-queues a follow-up bump 5 days after every send if no reply (you can opt out per-item). Live green pulse badge on the Auto-Pilot tab shows today's pipeline count from any screen. Pause/resume the entire pipeline with one click. Every prospect card AND every voucher coalition card now has a <strong>"Schedule via Auto-Pilot"</strong> button that pre-fills everything for you.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Generate Pitch (Prospects tab)</strong>
                <p>Click "Generate Pitch" on any prospect card. The system auto-fills their company name, decision maker, and calculates their vacancy loss numbers. Copy it to your clipboard or send it as an email — one click.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Sales Pipeline View (Prospects tab)</strong>
                <p>Toggle to "Sales Pipeline" to see all 30+ prospects on a kanban board: Cold, Contacted, Responded, Meeting, Won, Lost. Drag prospects through your sales process with the dropdown on each card.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Follow-Up Tracker (Prospects tab)</strong>
                <p>Prospects you pitched 5+ days ago with no response now show up in an orange "Follow-Up Needed" section at the top. Hit "Follow Up" to re-generate a pitch for them instantly.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Copy to Clipboard (Contracts tab)</strong>
                <p>Every contract and pitch template now has a blue "Copy to Clipboard" button. Copy the full text and paste it directly into Gmail, Outlook, or any email app.</p>
              </div>
              <div className="coo-guide-item">
                <strong>DFW Market Pulse (Prospects tab)</strong>
                <p>Live DFW market stats at the top of your Prospects tab — vacancy rate, average rent, offline units, foreclosure pipeline. Numbers you can cite in every pitch.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Product Catalog (Products tab)</strong>
                <p>30 products across 8 categories — paint, flooring, fixtures, hardware, lighting, cleaning supplies, and MF gear. Every product has wholesale cost, retail price, and profit margin shown — use these to scope the per-unit material budget cap before pricing the engagement.</p>
              </div>
              <div className="coo-guide-item">
                <strong>How to Bill Clients for Materials</strong>
                <p>1. Shannon walks the unit and tells you exactly what it needs. 2. Send the client an itemized invoice via <strong>HoneyBook</strong>, a <strong>Stripe payment link</strong>, or direct <strong>ACH/wire</strong> — whichever fits the deal. 3. Funds land in your <strong>Relay MATERIALS</strong> account. 4. A designated <strong>Relay debit card</strong> is assigned per unit (or shared across a small batch of units). 5. Buy materials at wholesale through the pro accounts using that card. 6. Every unit keeps its own itemized material cost line in the project record. 7. The client never sees your wholesale cost — they only see the agreed line items.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Business Certifications (Certifications tab)</strong>
                <p>5 certification packages ready to go — WBE, MBE, DBE, SBA 8(a), and HUBZone. Each one has step-by-step instructions, a pre-written narrative, company info pre-filled, and a document checklist. Start with the free ones: DBE, 8(a), and HUBZone.</p>
              </div>
              <div className="coo-guide-item">
                <strong>Business Operations Guide (Launch tab — scroll down)</strong>
                <p>Full setup instructions for Relay banking (4-account structure: Materials, Operating, Payroll, Tax Reserve), how to connect HoneyBook to Relay, the HoneyBook / Stripe / direct-wire client billing flow, pro account discount tracking, and how Relay debit cards get assigned per unit. Shannon said you'll be managing this.</p>
              </div>
              {(dashboardRole === "coo" || dashboardRole === "administrator") && (
                <div className="coo-guide-item">
                  <strong>Your Pitch Templates (Contracts tab)</strong>
                  <p>You now have access to ALL pitch templates — Institutional, SFR, Distressed, Grassroots, Individual Landlord, DHA, FWHS — plus Cold Outreach emails and the Pilot Program. Everything Shannon has, you have too.</p>
                </div>
              )}
            </div>
            <button onClick={dismissWhatsNew} className="coo-guide-btn" data-testid="whats-new-dismiss">Got It</button>
          </div>
        </div>
      )}
      {/* 5-Hub simplified navigation (Shannon's founder vision, May 12, 2026) */}
      <div className="dash-hub-bar" data-testid="dashboard-hub-bar" style={{
        display: "flex",
        gap: 8,
        padding: "10px 18px 12px",
        background: "linear-gradient(180deg, rgba(3,11,26,0.95) 0%, rgba(3,11,26,0.85) 100%)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        overflowX: "auto",
        flexWrap: "nowrap",
        alignItems: "center"
      }}>
        {HUBS.map(h => (
          <button
            key={h.key}
            onClick={() => setHub(h.key)}
            data-testid={`hub-${h.key}`}
            title={h.desc}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 999,
              border: `1.5px solid ${activeHub === h.key ? h.color : "rgba(255,255,255,0.10)"}`,
              background: activeHub === h.key ? `${h.color}1a` : "rgba(255,255,255,0.02)",
              color: activeHub === h.key ? h.color : "#ADBED1",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.18s",
              letterSpacing: "0.01em",
            }}
          >
            <i className={`fas ${h.icon}`} style={{ fontSize: 12 }}></i>
            {h.label}
          </button>
        ))}
      </div>
      <nav className="dash-nav">
        <div className="dash-nav-left">
          <img src={LOGO_URL} alt="Logo" className="dash-nav-logo" />
          <span className="dash-nav-brand">{getRoleLabel(dashboardRole)}</span>
        </div>
        <div className="dash-tabs">
          {hubFilteredTabs.map((t, i) =>
            t.divider ? null : (
              <button
                key={t.key}
                className={`dash-tab dash-tab-cat-${t.cat || "core"} ${t.homeStyle ? "dash-tab-home " : ""}${tab === t.key ? "active" : ""}`}
                onClick={() => t.externalPath ? navigate(t.externalPath) : setTab(t.key)}
                data-testid={`tab-${t.key}`}
              >
                {t.label}
                {t.key === "auto-pilot" && autoPilotBadge.count > 0 && (
                  <span
                    data-testid="auto-pilot-nav-badge"
                    title={autoPilotBadge.is_paused ? `${autoPilotBadge.count} queued today (Paused)` : `${autoPilotBadge.count} firing today`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      marginLeft: 6,
                      borderRadius: 9,
                      background: autoPilotBadge.is_paused ? "#FFB347" : "#00DC78",
                      color: "#030B1A",
                      fontSize: 10,
                      fontWeight: 800,
                      lineHeight: 1,
                      boxShadow: autoPilotBadge.is_paused ? "none" : "0 0 0 0 rgba(0,220,120,0.55)",
                      animation: autoPilotBadge.is_paused ? "none" : "mfPulse 2.2s infinite"
                    }}
                  >
                    {autoPilotBadge.count}
                  </span>
                )}
              </button>
            )
          )}
        </div>
        <div className="dash-nav-right">
          <div className="dash-search-wrap" data-testid="dashboard-search">
            <input
              type="text"
              placeholder="Search anything... (e.g. prospects, design, payroll)"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              className="dash-search-input"
              data-testid="search-input"
            />
            {searchResults.length > 0 && (
              <div className="dash-search-results" data-testid="search-results">
                {searchResults.map((r, i) => (
                  <button key={i} onClick={() => handleSearchSelect(r)} className="dash-search-result" data-testid={`search-result-${i}`}>
                    <span className={`search-type search-type-${r.type}`}>{r.type === "tab" ? "Tab" : r.type === "contract" ? "Doc" : "Tool"}</span>
                    <div className="search-result-text">
                      <strong>{r.label}</strong>
                      <span>{r.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {(dashboardRole === "ceo" || dashboardRole === "coo" || dashboardRole === "administrator") && (
            <button onClick={() => setTab("home")} className={`dash-bell-btn ${unreadShared > 0 ? "dash-bell-active" : ""}`} data-testid="notification-bell" title="Shared items">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              {unreadShared > 0 && <span className="dash-bell-count">{unreadShared}</span>}
            </button>
          )}
          <button
            onClick={toggleTrainingMode}
            className={`dash-guide-btn ${trainingMode ? "training-on-btn" : "training-off-btn"}`}
            data-testid="training-mode-toggle"
            title={trainingMode ? "Training tips are ON — click to hide them once you've got it down" : "Training tips are OFF — click to show them again"}
            style={{ background: trainingMode ? "rgba(13,148,136,0.15)" : "transparent", border: `1px solid ${trainingMode ? "#0d9488" : "rgba(255,255,255,0.25)"}`, color: trainingMode ? "#0d9488" : "rgba(255,255,255,0.55)" }}
          >
            {trainingMode ? "Training: ON" : "Training: OFF"}
          </button>
          <button onClick={() => setShowQuickStart(true)} className="dash-guide-btn" data-testid="show-guide-btn">Guide</button>
          <button
            onClick={() => setShowPreflight(true)}
            data-testid="preflight-launch-btn"
            title={
              preflightOverall === "green" ? "Pre-Flight: ALL GREEN — system ready" :
              preflightOverall === "yellow" ? "Pre-Flight: YELLOW — watch-items present (click for detail)" :
              preflightOverall === "red" ? "Pre-Flight: RED — blockers present (click for detail)" :
              "Pre-Flight Deploy Check — click to scan"
            }
            style={{
              background: preflightOverall === "red"
                ? "rgba(220,38,38,0.18)"
                : preflightOverall === "yellow"
                ? "rgba(255,179,71,0.18)"
                : preflightOverall === "green"
                ? "rgba(34,197,94,0.18)"
                : "rgba(184,145,90,0.15)",
              border: `1px solid ${
                preflightOverall === "red" ? "rgba(220,38,38,0.6)" :
                preflightOverall === "yellow" ? "rgba(255,179,71,0.6)" :
                preflightOverall === "green" ? "rgba(34,197,94,0.6)" :
                "rgba(184,145,90,0.55)"
              }`,
              color: preflightOverall === "red" ? "#FCA5A5"
                : preflightOverall === "yellow" ? "#FFB347"
                : preflightOverall === "green" ? "#86EFAC"
                : "#B8915A",
              padding: "0.4rem 0.85rem",
              borderRadius: 5,
              fontWeight: 700,
              fontSize: "0.78rem",
              cursor: "pointer",
              letterSpacing: "0.06em",
              whiteSpace: "nowrap",
            }}
          >
            {preflightOverall === "red" ? "🔴" : preflightOverall === "yellow" ? "🟡" : preflightOverall === "green" ? "🟢" : "🔒"} Pre-Flight
          </button>
          <a
            href="/?marketing=1"
            target="_blank"
            rel="noopener noreferrer"
            className="dash-guide-btn"
            data-testid="marketing-site-link"
            title="Open the public moovinforward.org landing page in a new tab (great for showing prospects without logging out)"
            style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <i className="fas fa-external-link-alt" style={{ fontSize: 10 }}></i>
            Marketing Site
          </a>
          <span className="dash-user">{user.name}</span>
        </div>
      </nav>
      <SandboxBanner />
      <PreflightModal open={showPreflight} onClose={() => setShowPreflight(false)} />
      <DashboardAgent />
      <main className="dash-main">
        {tab === "home" && <Cockpit setTab={setTab} dashboardRole={dashboardRole} currentUser={user} />}
        {tab === "hub-home" && <HomeTab setTab={setTab} dashboardRole={dashboardRole} />}
        {tab === "tools" && <FieldToolsTab />}
        {tab === "contracts" && <ContractsTab dashboardRole={dashboardRole} />}
        {tab === "prospects" && <ProspectsTab setTab={setTab} />}
        {tab === "pitch-center" && <PitchCenterTab />}
        {tab === "grants" && <GrantsTab />}
        {tab === "lead-paint-roster" && <LeadPaintRosterTab />}
        {tab === "auto-pilot" && <OutreachAutoPilotTab />}
        {tab === "voucher-coalitions" && <VoucherCoalitionsTab setTab={setTab} />}
        {tab === "messages" && <ClientMessagesTab />}
        {tab === "sourced" && <SourcedProductsTab />}
        {tab === "founder-draw" && <FounderDrawTab />}
        {tab === "scripts" && <ScriptsLibraryTab />}
        {tab === "vendor-network" && <VendorNetworkTab setTab={setTab} />}
        {tab === "ready-roster" && <ReadyRosterTab setTab={setTab} />}
        {tab === "pro-desks" && <ProDesksTab />}
        {tab === "field-mode" && <><InspectionHome embedded /><FieldLauncherTab /></>}
        {tab === "launch" && <LaunchChecklistTab />}
        {tab === "brief-requests" && <BriefRequestsTab />}
        {tab === "operations" && <OperationsTab />}
        {tab === "compliance" && <ComplianceTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "clients" && <ClientsTab user={user} />}
        {tab === "payroll" && <PayrollTab />}
        {tab === "tasks" && <TaskBoardTab />}
        {tab === "revenue" && <RevenueTab />}
        {tab === "certifications" && <CertificationsTab />}
        {tab === "schedule" && <CrewScheduleTab />}
        {tab === "templates-library" && <TemplatesLibraryTab />}
        {tab === "palette-spotlight" && <PaletteSpotlightTab />}
      </main>
      <MoovinAiAssistant />
      <QuickCaptureFAB />
    </div>
  );
};

export default Dashboard;
